import React, { useEffect, useMemo, useRef } from 'react';
import { MenuItem, Select } from '@mui/material';
import { POSITIONS_COLOR } from '../../utils/constants';
import {
  TeamPicksPanelWrap,
  TeamPicksPanelHeader,
  TeamPicksTab,
  TeamPicksPanelTeam,
  TeamPicksTeamStatic,
  TeamPicksTeamOption,
  TeamPicksPanelBody,
  TeamPicksTableHeader,
  TeamPicksRow,
  TeamPicksCell,
  TeamPicksPickCol,
  TeamPicksPickNum,
  TeamPicksRoundNum,
  TeamPicksPlayerCol,
  TeamPicksPlayerName,
  TeamPicksPlayerSchool,
  TeamPicksPositionBadge,
  TeamPicksRank,
  TeamPicksEmpty,
} from './DraftBoard.styles';

/** 'R1', or 'R1 · I2' once a draft runs more than one iteration. */
const formatRound = (item, showIteration) => {
  if (item.round_index_number == null) return '—';
  const round = `R${item.round_index_number}`;
  return showIteration ? `${round} · I${item.iteration ?? 1}` : round;
};

/**
 * The table's single source of truth: one entry per column, in display order.
 * Add, remove or reorder an entry and the header and every row follow together.
 *
 *   key    — React key, unique within this list
 *   label  — header text
 *   width  — a CSS grid track (e.g. '56px', 'minmax(0, 1fr)' to take the slack)
 *   align  — text-align for the header cell and the row cell; defaults to left
 *   render — cell content for one pick
 */
const buildTeamPicksColumns = ({ showIteration, boardRankById }) => [
  {
    key: 'pick',
    label: 'Pick',
    width: showIteration ? 'clamp(40px, 2.3vw, 96px)' : 'clamp(30px, 1.8vw, 72px)',
    align: 'left',
    // Round folded into this cell, stacked under the pick number the way the player cell stacks
    // school under name — one column back for the player name, and the round reads as secondary.
    render: (item) => (
      <TeamPicksPickCol>
        <TeamPicksPickNum>{item.index}</TeamPicksPickNum>
        <TeamPicksRoundNum>{formatRound(item, showIteration)}</TeamPicksRoundNum>
      </TeamPicksPickCol>
    ),
  },
  {
    key: 'player',
    label: 'Player',
    width: 'minmax(0, 1fr)',
    render: (item) => (
      <TeamPicksPlayerCol>
        <TeamPicksPlayerName>{item.player?.player || 'Upcoming'}</TeamPicksPlayerName>
        {item.player?.school && (
          <TeamPicksPlayerSchool>{item.player.school}</TeamPicksPlayerSchool>
        )}
      </TeamPicksPlayerCol>
    ),
  },
  {
    key: 'position',
    label: 'Position',
    width: 'clamp(56px, 3.6vw, 134px)',
    align: 'center',
    render: (item) => (
      <TeamPicksPositionBadge $color={POSITIONS_COLOR[item.player?.position]}>
        {item.player?.position || '—'}
      </TeamPicksPositionBadge>
    ),
  },
  {
    key: 'rank',
    label: 'BPA',
    width: 'clamp(44px, 2.1vw, 106px)',
    align: 'center',
    // BPA frozen at the moment of the pick (client: "where he is at time of pick is best").
    // Looking it up live would drift — the server re-ranks the board after every pick, so a
    // pick from iteration 1 would keep changing number as the draft ran on. Falls back to the
    // team's current board position, then the site ranking, for picks with no stamp.
    render: (item) => (
      <TeamPicksRank>
        {item.boardBpaAtPick ?? boardRankById?.get(item.player?.id) ?? item.player?.ranking ?? '—'}
      </TeamPicksRank>
    ),
  },
  {
    key: 'adp',
    label: 'ADP',
    width: 'clamp(52px, 2.6vw, 125px)',
    align: 'center',
    // Frozen at the moment of the pick, same as BPA. This is the value the player carried going
    // into the pick — the ADP move happens on this very pick, and the board keeps shifting after.
    // Three decimals: the midpoint formula produces long fractions.
    render: (item) => (
      <TeamPicksRank>
        {item.adpAtPick != null ? Math.round(item.adpAtPick * 1000) / 1000 : '—'}
      </TeamPicksRank>
    ),
  },
];


/* Matches the pill dropdown used in TradeModal, on MUI's Select so the menu
   portals out of the panel's `overflow: hidden` instead of being clipped. */
const teamSelectSx = {
  flex: 1,
  minWidth: 0,
  borderRadius: '10px',
  background: '#fff',

  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    padding: 'clamp(6px, 0.4vw, 14px) clamp(34px, 2.2vw, 78px) clamp(6px, 0.4vw, 14px) clamp(10px, 0.63vw, 24px)',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cad2da', borderWidth: '1.5px' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#99acbfff' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#99acbfff', borderWidth: '1.5px' },
  '& .MuiSelect-icon': {
    color: '#50647b',
    fontSize: 'clamp(24px, 1.5vw, 56px)',
    right: 'clamp(6px, 0.4vw, 14px)',
  },
};

const teamMenuProps = {
  PaperProps: {
    sx: {
      maxHeight: '60vh',
      borderRadius: '12px',
      border: '1.5px solid #cad2da',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
      '& .MuiMenuItem-root': {
        padding: 'clamp(8px, 0.5vw, 19px) clamp(14px, 0.88vw, 34px)',
        '&:hover': { background: '#f1f5f9' },
        '&.Mui-selected': { background: '#eff6ff' },
        '&.Mui-selected:hover': { background: '#e2edfb' },
      },
    },
  },
};

const TeamPicksPanel = ({
  team, tab, onTabChange, teamPicks, boardRankById, nextPickKey, showIteration, top,
  userTeams = [], onTeamChange,
  // Mobile: DraftBoard's tabs show this panel or the round board, never both.
  mobileHidden = false,
}) => {
  const isYours = tab === 'your';

  // Auto-scroll, like the round board on the left: keep the first upcoming pick in view — the
  // pick on the clock in Team Picks, the team's next pick in Your Picks — or the last row once
  // every pick is made. Only the panel body scrolls; scrollIntoView would move the page too.
  const bodyRef = useRef(null);
  const targetRowRef = useRef(null);
  const targetPick = teamPicks.find((item) => !item.player?.player) ?? teamPicks.at(-1);
  const targetKey = targetPick ? (targetPick.index_position ?? targetPick.index) : null;
  useEffect(() => {
    const body = bodyRef.current;
    const row = targetRowRef.current;
    if (!body || !row) return;
    // Align the row's bottom with the body's bottom (block: 'end', as the left panel does).
    const delta = row.getBoundingClientRect().bottom - body.getBoundingClientRect().bottom;
    body.scrollTo({ top: body.scrollTop + delta, behavior: 'smooth' });
  }, [targetKey, tab, team?.id]);

  // An empty row is a pick not yet made, in either tab — "Upcoming" says that, an em dash does not.
  const columns = useMemo(
    () => buildTeamPicksColumns({ showIteration, boardRankById }),
    [showIteration, boardRankById]
  );
  const gridTemplate = columns.map((col) => col.width).join(' ');

  return (
    <TeamPicksPanelWrap $top={top} $mobileHidden={mobileHidden} aria-label="Team picks">
      <TeamPicksPanelHeader>
        <TeamPicksTab $active={isYours} onClick={() => onTabChange('your')}>
          Your Picks
        </TeamPicksTab>
        <TeamPicksTab $active={!isYours} onClick={() => onTabChange('team')}>
          Team Picks
        </TeamPicksTab>
      </TeamPicksPanelHeader>

      {/* Team Picks has no selector — it tracks the clock on its own (client: it must match the
          team on the clock without the user having to click or change the display). Your Picks
          keeps one, so a user with several teams can still look across them. */}
      {team && (
        <TeamPicksPanelTeam>
          {isYours && userTeams.length > 1 ? (
            <Select
              value={team.id}
              onChange={(event) => onTeamChange(event.target.value)}
              size="small"
              sx={teamSelectSx}
              MenuProps={teamMenuProps}
              inputProps={{ 'aria-label': 'Show picks for team' }}
              renderValue={() => (
                <TeamPicksTeamOption>
                  <img src={team.logo} alt="" />
                  <span>{team.name}</span>
                </TeamPicksTeamOption>
              )}
            >
              {userTeams.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <TeamPicksTeamOption>
                    <img src={option.logo} alt="" />
                    <span>{option.name}</span>
                  </TeamPicksTeamOption>
                </MenuItem>
              ))}
            </Select>
          ) : (
            <TeamPicksTeamStatic>
              {team.logo && <img src={team.logo} alt="" />}
              <span>{team.name}</span>
            </TeamPicksTeamStatic>
          )}
        </TeamPicksPanelTeam>
      )}

      <TeamPicksPanelBody ref={bodyRef}>
        <TeamPicksTableHeader $template={gridTemplate}>
          {columns.map((col) => (
            <TeamPicksCell key={col.key} $align={col.align}>
              {col.label}
            </TeamPicksCell>
          ))}
        </TeamPicksTableHeader>

        {teamPicks.length === 0 && <TeamPicksEmpty>No picks for this team.</TeamPicksEmpty>}

        {teamPicks.map((item) => {
          const pickKey = item.index_position ?? item.index;
          return (
            <TeamPicksRow
              key={pickKey}
              ref={pickKey === targetKey ? targetRowRef : null}
              $template={gridTemplate}
              $isNext={pickKey === nextPickKey}
            >
              {columns.map((col) => (
                <TeamPicksCell key={col.key} $align={col.align}>
                  {col.render(item)}
                </TeamPicksCell>
              ))}
            </TeamPicksRow>
          );
        })}
      </TeamPicksPanelBody>
    </TeamPicksPanelWrap>
  );
};

export default TeamPicksPanel;
